import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Home, Wallet, TrendingDown, Target, Calendar, Sparkles } from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

const SCOPES = [
    { key: "together",  label: "Together" },
    { key: "mine",      label: "Mine" },
    { key: "partner",   label: "Partner" },
    { key: "joint",     label: "Joint" },
    { key: "household", label: "Household" },
];

function fmtINR(v) {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (n >= 100000)  return `₹${(n / 100000).toFixed(2)} L`;
    if (n >= 1000)    return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
}

export default function HouseholdMoneyMap() {
    const [scope, setScope] = useState("together");
    const [together, setTogether] = useState(null);
    const [summary, setSummary]   = useState(null);
    const [goals, setGoals]       = useState([]);
    const [calendar, setCalendar] = useState([]);

    useEffect(() => {
        api.get("/household/together", { params: { view: scope } })
           .then(r => setTogether(r.data)).catch(() => setTogether(null));
    }, [scope]);

    useEffect(() => {
        Promise.all([
            api.get("/household/summary").catch(() => null),
            api.get("/household/goals").catch(() => null),
            api.get("/household/calendar", { params: { scope: "shared" } }).catch(() => null),
        ]).then(([s, g, c]) => {
            setSummary(s?.data?.sections || null);
            setGoals((g?.data?.goals || []).filter(x => x.kind !== "Personal").slice(0, 6));
            setCalendar((c?.data?.events || []).slice(0, 8));
        });
    }, []);

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="household-money-map-page">
            <HouseholdNav />

            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                    <Home size={12} /> Money Map · Together
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                    Your household financial world, side-by-side.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    What We Own · What We Owe · What Is Coming · Shared Goals · Shared Obligations —
                    every private record filtered out server-side.
                </p>
            </div>

            <div className="flex flex-wrap gap-1 border border-stone-200 rounded-full p-1 bg-white w-fit mb-4"
                 data-testid="mm-scope">
                {SCOPES.map(s => (
                    <button key={s.key} onClick={() => setScope(s.key)}
                            className={`px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${scope === s.key ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                            data-testid={`mm-scope-${s.key}`}>
                        {s.label}
                    </button>
                ))}
            </div>

            {summary && (
                <section className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6" data-testid="mm-position">
                    {[
                        { k: "assets_value",       l: "What We Own",  icon: Wallet,       tone: "text-emerald-800 border-emerald-200 bg-emerald-50/40" },
                        { k: "liabilities_value",  l: "What We Owe",  icon: TrendingDown, tone: "text-rose-800 border-rose-200 bg-rose-50/40" },
                        { k: "investments_value",  l: "Wealth",       icon: Sparkles,     tone: "text-amber-800 border-amber-200 bg-amber-50/40" },
                        { k: "household_net_worth",l: "Net Worth",    icon: Home,         tone: "text-stone-900 border-stone-200 bg-stone-50/40" },
                    ].map(t => {
                        const Icon = t.icon;
                        return (
                            <div key={t.k} className={`rounded-2xl border ${t.tone} p-4`} data-testid={`mm-${t.k}`}>
                                <Icon size={14} className="opacity-80 mb-1.5" />
                                <div className="text-[10.5px] uppercase tracking-widest font-semibold">{t.l}</div>
                                <div className="text-2xl font-display tabular-nums mt-1">{fmtINR(summary.shared_position[t.k])}</div>
                            </div>
                        );
                    })}
                </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
                <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="mm-obligations">
                    <div className="flex items-center gap-2 mb-2">
                        <Calendar size={13} className="text-stone-500" />
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Shared obligations · next 90d</div>
                        <Link to="/household/checkin" className="ml-auto text-[11px] font-semibold text-amber-800">Open check-in →</Link>
                    </div>
                    {calendar.length === 0 ? (
                        <div className="text-[13px] text-stone-500">No upcoming shared obligations.</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {calendar.map((e, i) => (
                                <li key={i} className="flex items-center gap-2 text-[13px] text-stone-800">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    <span className="capitalize font-semibold text-[11px] text-stone-500 uppercase tracking-widest w-20">{e.kind}</span>
                                    <span className="flex-1 truncate">{e.title}</span>
                                    <span className="text-[11px] text-stone-500 tabular-nums">{e.on}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="mm-goals">
                    <div className="flex items-center gap-2 mb-2">
                        <Target size={13} className="text-stone-500" />
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Shared goals</div>
                        <Link to="/household/goals" className="ml-auto text-[11px] font-semibold text-amber-800">Open goals →</Link>
                    </div>
                    {goals.length === 0 ? (
                        <div className="text-[13px] text-stone-500">No couple or family goals yet.</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {goals.map(g => (
                                <li key={g.id} className="flex items-center gap-2 text-[13px] text-stone-800">
                                    <span className="text-[10.5px] uppercase tracking-widest text-amber-800 font-semibold w-16">{g.kind}</span>
                                    <span className="flex-1 truncate">{g.title}</span>
                                    <span className="text-[11px] text-stone-500 tabular-nums">
                                        {g.target_amount ? fmtINR(g.target_amount) : "no target"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {together && (
                <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="mm-modules">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Records in scope · {scope}</div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
                        {Object.entries(together.modules).map(([mod, data]) => (
                            <div key={mod} className="border border-stone-200 rounded-xl p-3" data-testid={`mm-mod-${mod}`}>
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold truncate">{mod}</div>
                                <div className="text-2xl font-display tabular-nums text-stone-900 mt-1">{data.count}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
